import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { patents } from '../data/mockData';
import { trackButtonClick } from '../utils/trackButtonClick';
import CinematicThemeSwitcher from './ui/cinematic-theme-switcher';

// Maps a nav section id to a single Tailwind background-color class for its
// rail line — mirrors the same per-section accent colors ThemeContext.js's
// `accents` object already defines. The rail only ever needs one color per
// section — active vs. inactive is expressed with opacity in the render,
// not a different color — so this returns a plain string, not a
// {bg, text} pair.
export function getSectionColor(sectionId, isDark) {
  const map = {
    hero: isDark ? 'bg-blue-400' : 'bg-indigo-600',
    about: isDark ? 'bg-green-400' : 'bg-emerald-600',
    patents: isDark ? 'bg-amber-400' : 'bg-amber-600',
    skills: isDark ? 'bg-purple-400' : 'bg-violet-600',
    experience: isDark ? 'bg-orange-400' : 'bg-amber-600',
    projects: isDark ? 'bg-cyan-400' : 'bg-cyan-600',
    contact: isDark ? 'bg-pink-400' : 'bg-rose-600',
  };
  return map[sectionId] || (isDark ? 'bg-gray-400' : 'bg-slate-500');
}

// No icon field: the rail expresses each section as a colored line, not an
// icon, so there's nothing here that needs a per-item icon component the
// way the previous redesign's panel did.
const NAV_ITEMS = [
  { id: 'hero', label: 'Home' },
  { id: 'about', label: 'Who Am I' },
  ...(patents.length >= 1 ? [{ id: 'patents', label: 'Patents' }] : []),
  { id: 'skills', label: 'Skills' },
  { id: 'experience', label: 'Career' },
  { id: 'projects', label: 'Projects' },
  { id: 'contact', label: 'Contact' },
];

// How long the rail stays fully visible after the last scroll event before
// dimming back down — mirrors the "stopped scrolling" half of the idle
// condition; hovering or touching the rail suppresses dimming entirely,
// independent of this timer (see keepAwake below).
const IDLE_DIM_DELAY_MS = 1500;

const FloatingNav = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [previewSection, setPreviewSection] = useState(null);
  // Whole-rail idle state: dimmed (30% opacity, i.e. dimmed by 70%) once the
  // user has stopped scrolling AND isn't hovering/touching the rail — not to
  // be confused with the per-line active/preview/inactive opacity below,
  // which only matters once the rail itself is already visible.
  const [isIdle, setIsIdle] = useState(false);
  const idleTimerRef = useRef(null);
  const railRef = useRef(null);
  const lineRefs = useRef([]);
  const { isDark } = useTheme();

  // Starts (or restarts) the countdown to dimming. Used after a punctual
  // event (scroll) and after a continuous engagement (hover/touch) ends.
  const scheduleDim = () => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIsIdle(true), IDLE_DIM_DELAY_MS);
  };

  // Un-dims and cancels any pending dim, without rescheduling — used for
  // continuous engagement (mouse still over the rail, finger still down) so
  // a stale timer from before the engagement started can't dim it out from
  // under the user's cursor/finger.
  const keepAwake = () => {
    setIsIdle(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  };

  // Un-dims and restarts the countdown — used for punctual events (each
  // scroll tick) rather than a held engagement.
  const wake = () => {
    setIsIdle(false);
    scheduleDim();
  };

  useEffect(() => {
    const handleScroll = () => {
      const current = NAV_ITEMS.find((item) => {
        const element = document.getElementById(item.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current.id);
      wake();
    };
    window.addEventListener('scroll', handleScroll);
    // Starts the rail fully visible on mount, dimming after IDLE_DIM_DELAY_MS
    // if nothing (scroll/hover/touch) happens before then.
    scheduleDim();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  const scrollToSection = (sectionId, smooth = true) => {
    const element = document.getElementById(sectionId);
    element?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  // Touch-drag scrubbing: press anywhere on the rail and drag, and the
  // active section (and the page's scroll position) follows the finger
  // continuously — the mobile equivalent of the desktop hover+click above,
  // modeled on iOS Contacts' A-Z index scrubber.
  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;

    // Given a touch's Y position, find the nearest rail line — this is what
    // makes the whole rail draggable-over rather than requiring
    // pixel-precise taps on each thin line.
    const findNearestItem = (clientY) => {
      let nearest = null;
      let nearestDistance = Infinity;
      lineRefs.current.forEach((el, index) => {
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const centerY = rect.top + rect.height / 2;
        const distance = Math.abs(clientY - centerY);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearest = NAV_ITEMS[index];
        }
      });
      return nearest;
    };

    let lastItem = null;

    // Registered as a native listener with { passive: false } rather than a
    // JSX onTouchMove prop — React attaches touch listeners as passive by
    // default, which would silently make e.preventDefault() below a no-op,
    // and preventDefault is what stops the browser's own touch-scroll from
    // fighting the instant scroll this handler drives manually.
    const handleTouchPoint = (e) => {
      e.preventDefault();
      keepAwake();
      const touch = e.touches[0];
      const item = findNearestItem(touch.clientY);
      // Skip redundant work while the finger sits within the same line's
      // nearest-zone across consecutive move events — the state setters are
      // cheap no-ops when unchanged, but scrollToSection forces a fresh
      // getElementById + scrollIntoView layout pass every call regardless.
      if (!item || item === lastItem) return;
      lastItem = item;
      setPreviewSection(item.id);
      setActiveSection(item.id);
      scrollToSection(item.id, false);
    };

    // Deliberately not calling trackButtonClick from handleTouchPoint
    // itself — that fires on every touchmove, which would be dozens of
    // analytics events for one drag gesture. Instead, track whichever
    // section the finger last landed on and fire once when the gesture
    // ends, matching handleLineClick's one-event-per-navigation shape.
    const handleTouchEnd = () => {
      setPreviewSection(null);
      if (lastItem) {
        trackButtonClick({ section: 'nav', label: lastItem.label, type: 'nav_link' });
      }
      // Without this, a new gesture starting on the same line the previous
      // one ended on would see item === lastItem on its very first touch
      // point and skip the dedup guard's "show the preview" branch entirely
      // — the guard is meant to dedupe consecutive events within one
      // gesture, not carry state across separate gestures.
      lastItem = null;
      scheduleDim();
    };

    rail.addEventListener('touchstart', handleTouchPoint, { passive: false });
    rail.addEventListener('touchmove', handleTouchPoint, { passive: false });
    rail.addEventListener('touchend', handleTouchEnd);
    // Without this, an interrupted gesture (incoming call, notification
    // pull-down, an edge-swipe-back gesture the OS claims instead) never
    // fires touchend, leaving the last-scrubbed section's tooltip visibly
    // stuck on screen with nothing left to clear it.
    rail.addEventListener('touchcancel', handleTouchEnd);

    return () => {
      rail.removeEventListener('touchstart', handleTouchPoint);
      rail.removeEventListener('touchmove', handleTouchPoint);
      rail.removeEventListener('touchend', handleTouchEnd);
      rail.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, []);

  const handleLineClick = (item) => {
    trackButtonClick({ section: 'nav', label: item.label, type: 'nav_link' });
    scrollToSection(item.id, true);
  };

  return (
    <>
      <div
        ref={railRef}
        style={{ touchAction: 'none' }}
        onMouseEnter={keepAwake}
        onMouseLeave={scheduleDim}
        className={`fixed right-4 top-1/2 -translate-y-1/2 z-40 flex flex-col items-end gap-12.0 transition-opacity duration-500 ${
          isIdle ? 'opacity-30' : 'opacity-100'
        }`}
      >
        {NAV_ITEMS.map((item, index) => {
          const isActive = activeSection === item.id;
          const isPreviewed = previewSection === item.id;
          const color = getSectionColor(item.id, isDark);

          return (
            <div key={item.id} className="relative flex items-center">
              {isPreviewed && (
                <span
                  className={`absolute right-full mr-2 px-2 py-1 rounded-md text-xs whitespace-nowrap pointer-events-none ${
                    isDark
                      ? 'bg-black/85 text-white border border-white/10'
                      : 'bg-white/95 text-slate-900 border border-slate-200 shadow'
                  }`}
                >
                  {item.label}
                </span>
              )}
              <button
                ref={(el) => { lineRefs.current[index] = el; }}
                onClick={() => handleLineClick(item)}
                onMouseEnter={() => setPreviewSection(item.id)}
                onMouseLeave={() => setPreviewSection(null)}
                onFocus={() => { keepAwake(); setPreviewSection(item.id); }}
                onBlur={() => { scheduleDim(); setPreviewSection(null); }}
                aria-label={`Jump to ${item.label}`}
                aria-current={isActive ? 'true' : undefined}
                className={`py-2 px-1.5 rounded-full transition-opacity duration-200 ${
                  isActive ? 'opacity-100' : isPreviewed ? 'opacity-70' : 'opacity-40'
                }`}
              >
                <span
                  className={`block rounded-full transition-all duration-200 ${color} ${
                    isActive ? 'w-10 h-[7px]' : isPreviewed ? 'w-4 h-[7px]' : 'w-4 h-[7px]'
                  }`}
                />
              </button>
            </div>
          );
        })}
      </div>

      <div className="fixed right-3 top-3 z-30">
        <CinematicThemeSwitcher />
      </div>
    </>
  );
};

export default FloatingNav;
